<!--
  LwDialog: replaces `cDialog` and `modal.vue` (refresh_plan.md 1.5).

  Two defects this fixes at once (audit_web.md 3.4): `cDialog.vue:11-18`
  renders every action ("Save", "Delete", "Cancel") as a `<span @click>` with
  no tabindex, role or keydown handler, so Set Driver, Set Truck and Cancel
  Load on the Dispatch board are unreachable by keyboard even though Escape
  works there (`cDialog.vue:110-119`). `modal.vue` has the opposite defect: a
  backdrop click handler and no Escape handler at all. Two dialog components,
  two dismissal contracts. This component is the one dismissal contract:
  real `<button>` actions via LwButton, Escape, backdrop click, and a real
  focus trap, all in one place.

  It also becomes the replacement for `window.confirm()` on Team and
  Integrations (refresh_plan.md 1.5), which is why `variant="destructive"`
  exists: destructive confirms stop having two visual languages (the
  browser's native dialog chrome vs. this component) in one session.

  This phase builds the component only. No caller is migrated yet.
-->
<template>
  <div v-if="open" class="lw-c-dialog-scrim" @click="handleScrimClick">
    <div
      ref="dialog"
      class="lw-c-dialog"
      :class="{ 'lw-c-dialog--wide': size === 'wide' }"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      :aria-describedby="description ? descriptionId : null"
      tabindex="-1"
    >
      <h2 :id="titleId" class="lw-c-dialog__title">
        <slot name="title">
          {{ title }}
        </slot>
      </h2>

      <p v-if="description" :id="descriptionId" class="lw-c-dialog__description">
        {{ description }}
      </p>

      <div class="lw-c-dialog__body">
        <slot />
      </div>

      <div class="lw-c-dialog__actions">
        <slot name="actions" :confirm="handleConfirm" :cancel="handleCancel" :busy="busy">
          <LwButton variant="secondary" @click="handleCancel">
            {{ cancelLabel }}
          </LwButton>
          <LwButton :variant="confirmVariant" :loading="busy" @click="handleConfirm">
            {{ confirmLabel }}
          </LwButton>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import LwButton from './LwButton.vue';

// Module-level, like LwField's id counter: every instance gets a distinct,
// stable id pair for aria-labelledby / aria-describedby, generated once per
// instance rather than per open/close cycle.
let dialogIdCounter = 0;
function nextDialogId() {
  dialogIdCounter += 1;
  return dialogIdCounter;
}

// Standard focusable-element query for a manual focus trap. jsdom does not
// run layout, so offsetParent/visibility checks are deliberately not part of
// this selector: they would silently exclude every element in the test
// environment (jsdom's offsetParent is always null) without excluding
// anything meaningful in a real browser, since every element this component
// renders is visible whenever the dialog itself is open.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const VARIANTS = ['default', 'destructive'];

export default {
  name: 'LwDialog',
  components: { LwButton },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: null,
    },
    // 'destructive' colours the confirm action with the danger tokens
    // (contract). It exists so window.confirm()'s destructive prompts on
    // Team and Integrations stop looking different from this dialog's own
    // destructive confirms once they migrate (refresh_plan.md 1.5).
    variant: {
      type: String,
      default: 'default',
      validator: value => VARIANTS.includes(value),
    },
    confirmLabel: {
      type: String,
      default: 'Confirm',
    },
    cancelLabel: {
      type: String,
      default: 'Cancel',
    },
    busy: {
      type: Boolean,
      default: false,
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true,
    },
    // A CSS selector, resolved inside the dialog, for what to focus on open.
    // Falls back to the first focusable element, and then to the dialog
    // container itself (contract): focus must never be left on the page
    // behind the scrim.
    initialFocus: {
      type: String,
      default: null,
    },
    // 'wide' for a dialog whose body is a table (the dispatch board's driver
    // picker), where 480px would wrap every row. Everything else stays at
    // the confirm-sized default.
    size: {
      type: String,
      default: 'default',
      validator: value => value === 'default' || value === 'wide',
    },
  },
  emits: ['cancel', 'confirm'],
  data() {
    return {
      generatedId: nextDialogId(),
      previousActiveElement: null,
    };
  },
  computed: {
    titleId() {
      return `lw-dialog-title-${this.generatedId}`;
    },
    descriptionId() {
      return `lw-dialog-description-${this.generatedId}`;
    },
    confirmVariant() {
      return this.variant === 'destructive' ? 'destructive' : 'primary';
    },
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.handleOpen();
      } else {
        this.handleClose();
      }
    },
  },
  mounted() {
    // Covers the case where a parent mounts this component with open=true
    // from the start: the watcher above only fires on a subsequent change.
    if (this.open) this.handleOpen();
  },
  beforeUnmount() {
    // Two dialogs opened in one session must not leave two live document
    // listeners, and unmounting the component while it is open must not
    // strand focus on a detached element (contract). teardown()/restoreFocus()
    // are the same calls handleClose() makes, and are safe to call twice:
    // teardown() no-ops once the listener/lock are already gone.
    this.teardown();
    this.restoreFocus();
  },
  methods: {
    handleOpen() {
      if (typeof document === 'undefined') return;
      this.previousActiveElement = document.activeElement;
      this.lockBodyScroll();
      this.addKeydownListener();
      this.focusInitialElement();
    },
    handleClose() {
      this.teardown();
      this.restoreFocus();
    },
    teardown() {
      this.removeKeydownListener();
      this.unlockBodyScroll();
    },
    lockBodyScroll() {
      // Defensive per contract: jsdom and a server render must not throw.
      if (typeof document === 'undefined' || !document.body) return;
      if (this._scrollLocked) return;
      this._previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this._scrollLocked = true;
    },
    unlockBodyScroll() {
      if (typeof document === 'undefined' || !document.body) return;
      if (!this._scrollLocked) return;
      document.body.style.overflow = this._previousBodyOverflow;
      this._scrollLocked = false;
    },
    addKeydownListener() {
      if (typeof document === 'undefined') return;
      if (this._keydownHandler) return;
      this._keydownHandler = this.handleKeydown;
      document.addEventListener('keydown', this._keydownHandler);
    },
    removeKeydownListener() {
      if (typeof document === 'undefined') return;
      if (!this._keydownHandler) return;
      document.removeEventListener('keydown', this._keydownHandler);
      this._keydownHandler = null;
    },
    restoreFocus() {
      const el = this.previousActiveElement;
      this.previousActiveElement = null;
      if (
        el &&
        typeof el.focus === 'function' &&
        typeof document !== 'undefined' &&
        document.body &&
        document.body.contains(el)
      ) {
        el.focus();
      }
    },
    getFocusableElements() {
      if (!this.$refs.dialog) return [];
      return Array.from(this.$refs.dialog.querySelectorAll(FOCUSABLE_SELECTOR));
    },
    focusInitialElement() {
      // Deferred to the next tick because a watcher-driven open (open flips
      // false -> true after mount) fires before Vue has patched the v-if
      // block into the DOM; the dialog and its slot content are not
      // queryable yet on the same tick the watcher runs.
      this.$nextTick(() => {
        const dialogEl = this.$refs.dialog;
        if (!dialogEl) return;
        let target = this.initialFocus ? dialogEl.querySelector(this.initialFocus) : null;
        if (!target) {
          const focusable = this.getFocusableElements();
          target = focusable.length > 0 ? focusable[0] : dialogEl;
        }
        if (target && typeof target.focus === 'function') target.focus();
      });
    },
    handleKeydown(event) {
      if (event.key === 'Escape' || event.keyCode === 27) {
        this.$emit('cancel');
        return;
      }
      if (event.key === 'Tab' || event.keyCode === 9) {
        this.handleTabKey(event);
      }
    },
    // The trap: the focusable set is recomputed on every Tab press, not
    // cached at mount, because the body slot can change while the dialog is
    // open (contract). Every Tab press inside the dialog is intercepted and
    // resolved manually rather than only guarding the two wrap boundaries,
    // so the browser's own tab order (which would escape the dialog through
    // whatever the document's next focusable element happens to be) never
    // runs at all while the dialog is open.
    handleTabKey(event) {
      const focusable = this.getFocusableElements();
      if (focusable.length === 0) {
        event.preventDefault();
        if (this.$refs.dialog) this.$refs.dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const currentIndex = focusable.indexOf(document.activeElement);
      event.preventDefault();
      if (event.shiftKey) {
        const target = currentIndex <= 0 ? last : focusable[currentIndex - 1];
        target.focus();
      } else {
        const atEnd = currentIndex === -1 || currentIndex === focusable.length - 1;
        const target = atEnd ? first : focusable[currentIndex + 1];
        target.focus();
      }
    },
    // A click is only a backdrop click when it lands directly on the scrim,
    // never when it bubbles up from inside the dialog: checking
    // target === currentTarget means a click that starts and ends inside the
    // dialog never emits cancel, with no click.stop needed on the dialog
    // itself (contract: "a click inside the dialog never emits cancel").
    handleScrimClick(event) {
      if (event.target !== event.currentTarget) return;
      if (!this.closeOnBackdrop) return;
      this.$emit('cancel');
    },
    handleConfirm() {
      // Redundant with LwButton's own loading-blocks-click behaviour (it
      // never emits `click` while `loading`), but this method is also
      // reachable from the `actions` slot's scoped `confirm` callback, which
      // does not have to be wired to an LwButton at all.
      if (this.busy) return;
      this.$emit('confirm');
    },
    handleCancel() {
      this.$emit('cancel');
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../sass/mixins.generated';

.lw-c-dialog-scrim {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: var(--lw-z-dialog);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--lw-elev-scrim-surface);
  padding: var(--lw-space-4);
}

.lw-c-dialog {
  display: flex;
  flex-direction: column;
  width: 100%;
  // No token in the scale models a dialog's max width or height (hard rule
  // 2's stated exception, same as LwButton's spinner glyph): a raw px cap
  // stops a two-sentence confirm from stretching edge-to-edge on a wide
  // monitor, and the vh cap keeps a tall body scrolling inside the dialog
  // instead of the dialog itself running off the viewport.
  max-width: 480px;
  max-height: 90vh;

  &--wide {
    max-width: 760px;
  }

  overflow-y: auto;
  background: var(--lw-elev-4-surface);
  box-shadow: var(--lw-elev-4-shadow);
  border: 1px solid var(--lw-elev-4-line);
  // radius-lg: one step up from LwPanel's radius-md. A dialog (elev-4) is a
  // more prominent surface than a panel (elev-1), and today `.c-dialog`,
  // `.modal-content` and `.app-panel-content` all share the same absence of
  // shadow (contract: "a dialog must be visibly a different object from a
  // panel"); giving it the next radius step up keeps that distinction in the
  // corner too, not just the elevation.
  border-radius: var(--lw-radius-lg);
  padding: var(--lw-space-6);

  // The fallback focus target when the dialog has no focusable content
  // (contract): it needs its own visible ring for that case.
  @include lw-focus-visible;
}

.lw-c-dialog__title {
  margin: 0;
  font: var(--lw-type-subhead);
  color: var(--lw-fg-primary);
}

.lw-c-dialog__description {
  margin: var(--lw-space-2) 0 0;
  font: var(--lw-type-body-sm);
  color: var(--lw-fg-secondary);
}

.lw-c-dialog__body {
  margin-top: var(--lw-space-4);
  font: var(--lw-type-body);
  color: var(--lw-fg-primary);
}

.lw-c-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--lw-space-3);
  margin-top: var(--lw-space-6);
}
</style>
